import { IS_NATIVE, platform } from '@/platform';
import { logger } from '@/lib/logger';
import { onSessionWipe } from '@/lib/sessionWipe';
import { underSessionStoreLock } from '@/lib/sessionStoreLock';
import type { ActiveSession } from '@/lib/activeSessionUser';

/**
 * The app's outbox for photos of a job (M10): a photo taken where there is no
 * signal is kept, encrypted, and uploaded when the connection returns, even
 * after the app was force-stopped in between (assessment §R.9, §R.62).
 *
 * The bytes are kept through `platform.files` (AES-GCM, key in the Keystore;
 * kanfy-mobile), never in the clear; the entry describing them, in secure
 * storage. Both are made BEFORE the first attempt, so nothing a helper took
 * depends on the first attempt's answer.
 *
 * Exactly once rests on the upload operation (supabase/functions/_shared/
 * uploadOnce.ts): every attempt sends the same operation id and the same
 * cleaned bytes, and the function answers a repeat with the original success
 * rather than storing a second copy. Only endpoints whose deployed version
 * honours the operation id may be queued: `booking-evidence` is the one this
 * outbox takes. KYC, client ID and chat images are not queued.
 *
 * Nothing is reported as uploaded until the function says so. A refusal
 * (too large, wrong moment in the job, metadata) marks the photo "not
 * uploaded" with the reason; no answer, a 5xx or a busy lease (503) leaves it
 * waiting. A kept photo whose bytes can no longer be read (sign-out cleared
 * the key) is reported lost, never sent.
 */

export const UPLOAD_OUTBOX_KEY = 'outbox.uploads';
/** Where an outbox that could not be read is kept, rather than saved over (as Codex found on #1136). */
export const UPLOAD_OUTBOX_UNREADABLE_KEY = 'outbox.uploads.unreadable';
export const UPLOAD_OUTBOX_LIMIT = 20;
/**
 * Photos kept in all, waiting or not uploaded: refused photos do not hold up
 * new ones under UPLOAD_OUTBOX_LIMIT, but each keeps its bytes (up to 5 MB)
 * for Try again, so they are bounded too (Codex on #1137).
 */
export const UPLOAD_OUTBOX_TOTAL_LIMIT = 30;
/**
 * A photo refused for good is kept this long for Try again or Delete, then
 * dropped with its bytes: once its job leaves the list there may be no
 * screen left to delete it from (Codex on #1137).
 */
export const FAILED_UPLOAD_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/**
 * Ids of photos the function refused whose "not uploaded" state could not be
 * saved with the outbox. The saved outbox still says waiting, so a restart
 * would upload them on their own; this list, in plain prefs (ids only), keeps
 * them "not uploaded" until the helper chooses Try again.
 */
export const UPLOAD_REFUSED_PREF = 'upload-outbox.refused';
export const NOT_UPLOADED_PLAIN = 'This photo was not uploaded.';

export type UploadTarget = 'booking-evidence';

export interface UploadOutboxEntry {
  /** The upload operation id, kept for every attempt. */
  id: string;
  ownerId: string;
  target: UploadTarget;
  /** The function's form fields besides operationId and file. */
  fields: Record<string, string>;
  /** platform.files handle of the cleaned bytes. */
  handle: string;
  fileName: string;
  createdAt: string;
  state: 'queued' | 'failed';
  attempts: number;
  error?: string;
  /**
   * When it was refused: the week a refused photo is kept for Try again runs
   * from here, not from when it was taken (Codex on #1137).
   */
  failedAt?: string;
}

export type SendResult =
  | { ok: true }
  /**
   * `status`: the HTTP status, when the function answered. A transient
   * failure with an answer (a 5xx) may be this photo's own, so the next
   * photos are still tried; one with none (no connection) ends the pass.
   */
  | { ok: false; transient: boolean; message: string; status?: number };

export interface UploadOutboxDeps {
  /**
   * `accessToken`: the session the drain has just confirmed is the photo's
   * owner's, to send with instead of the client's current one (Codex on
   * #1137); absent only when no session check is configured.
   */
  send(entry: UploadOutboxEntry, file: File, accessToken?: string): Promise<SendResult>;
}

export const UPLOAD_LOST_MESSAGE = 'This photo could no longer be read on this phone. Please take it again.';

let deps: Partial<Record<UploadTarget, UploadOutboxDeps['send']>> = {};
let entries: UploadOutboxEntry[] | null = null;
/**
 * Handles of photos gone from the saved outbox whose bytes may still be on
 * the phone: a deletion that failed, or a force-stop before it ran. Saved in
 * the same write that drops the photo, and deleted again at each read and
 * drain until the deletion succeeds; counted under the total limit until
 * then (Codex on #1137).
 */
let trash: string[] = [];
let loading: Promise<void> | null = null;
let writes: Promise<void> = Promise.resolve();
let draining: Promise<void> | null = null;
// Every account a drain was asked for and has not yet run a pass for, in the
// order asked: a sign-in as someone else during a drain is not left to wait
// (Codex on #1137), and a stale timer for the account before cannot take its
// place (as Codex found on #1136). A pass for an account not signed in
// uploads nothing.
const drainOwners = new Set<string>();
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryAt = 0;
const retryOwners = new Set<string>();
/**
 * When each photo the function answered with a 5xx may be tried again: its
 * own backoff, so the photos after it are not held up behind it (Codex on
 * #1137). Kept in memory: a restart tries again at once.
 */
const notBefore = new Map<string, number>();
/**
 * The photo being uploaded, until its answer is handled: Delete waits for it
 * (Codex on #1137). Deleted mid-request, a photo could still be uploaded
 * while the card said it was gone.
 */
const inFlight = new Map<string, Promise<void>>();
/**
 * Photos a Delete has begun removing: the drain claims none of them, so a
 * deletion that started before the upload was claimed is never followed by
 * that upload (Codex on #1137). Taken in the same step as Delete's check for
 * an upload in flight, so one or the other always wins.
 */
const deleting = new Set<string>();
// Moved on by each sign-out: an enqueue that began before one keeps nothing.
let generation = 0;
/** files.put() calls whose entry is not saved yet; the stray sweep waits for none. */
let putsInFlight = 0;
/** The session whose stray bytes were swept, that sweep, and the one running now. */
let straysSwept = -1;
let strays: Promise<void> | null = null;
let sweeping: Promise<void> | null = null;
const listeners = new Set<(entries: readonly UploadOutboxEntry[]) => void>();
const doneListeners = new Set<(entry: UploadOutboxEntry) => void>();
interface Refusal {
  reason: string;
  /** When it was refused: its week runs from here. */
  at: string;
}
/**
 * Refused photos whose refusal no store has kept yet: neither the outbox save
 * nor the prefs mark succeeded. They are not shown as "not uploaded" until one
 * does, because a restart before then would upload them (Codex on #1136):
 * they stay shown as waiting, and are not uploaded in this run. Each drain
 * tries again to keep the refusal (Codex on #1137).
 */
const unsavedRefusals = new Map<string, Refusal>();

/** The refused marks are there but cannot be parsed: which ids they hold is unknown. */
class UnreadableRefusalsError extends Error {
  constructor() {
    super('The refused photos could not be read.');
    this.name = 'UnreadableRefusalsError';
  }
}

/**
 * The refused ids, each with when it was refused: the week a refused photo
 * is kept runs from then, across restarts, even when only this mark records
 * it (Codex on #1137). A prefs read that fails rejects (Codex on #1137):
 * taken as "none", a load would make a photo the card called "not uploaded"
 * waiting again, and upload it. Marks that cannot be parsed reject too, for
 * the same reason (as Codex found for the chat outbox on #1136); the load
 * then counts every waiting photo as refused.
 */
async function readRefused(): Promise<Map<string, string>> {
  const raw = await platform.storage.prefs.get(UPLOAD_REFUSED_PREF);
  let parsed: unknown;
  try {
    parsed = raw ? JSON.parse(raw) : {};
  } catch {
    throw new UnreadableRefusalsError();
  }
  // One mark that cannot be read is as unreadable as the whole: dropped, it
  // would make its photo waiting again, and upload it (as Codex found on #1136).
  const ids = new Map<string, string>();
  if (Array.isArray(parsed)) {
    // The mark before it kept the time: refused at the latest now.
    const now = new Date().toISOString();
    for (const id of parsed) {
      if (typeof id !== 'string') throw new UnreadableRefusalsError();
      ids.set(id, now);
    }
  } else if (parsed && typeof parsed === 'object') {
    for (const [id, at] of Object.entries(parsed)) {
      if (typeof at !== 'string' || Number.isNaN(Date.parse(at))) throw new UnreadableRefusalsError();
      ids.set(id, at);
    }
  } else {
    throw new UnreadableRefusalsError();
  }
  return ids;
}

async function writeRefused(ids: Map<string, string>): Promise<void> {
  try {
    await saveRefused(ids);
  } catch (error) {
    logger.warn('[upload-outbox] could not record a refused photo', error);
  }
}

/** Like writeRefused, but a failure rejects, for the caller that must know. */
async function saveRefused(ids: Map<string, string>): Promise<void> {
  if (ids.size > 0) await platform.storage.prefs.set(UPLOAD_REFUSED_PREF, JSON.stringify(Object.fromEntries(ids)));
  else await platform.storage.prefs.remove(UPLOAD_REFUSED_PREF);
}

let marksChain: Promise<unknown> = Promise.resolve();

/**
 * Every read-modify-write of the refused marks runs alone (Codex on #1137):
 * two at once would each save the marks without the other's change, and a
 * restart would then undo a Try again.
 */
function underMarks<T>(task: () => Promise<T>): Promise<T> {
  const run = marksChain.then(task);
  marksChain = run.catch(() => undefined);
  return run;
}

/** Reads the marks, changes them, and saves them if `change` says so, alone. */
function changeRefused(change: (ids: Map<string, string>) => boolean): Promise<void> {
  return underMarks(async () => {
    const ids = await readRefused();
    if (change(ids)) await saveRefused(ids);
  });
}

/** Like unmarkRefused, but a failure rejects, for the caller that must know. */
async function clearRefusedMark(id: string): Promise<void> {
  await changeRefused((ids) => ids.delete(id));
}

async function unmarkRefused(id: string): Promise<void> {
  try {
    await changeRefused((ids) => ids.delete(id));
  } catch (error) {
    logger.warn('[upload-outbox] could not update the refused photos', error);
  }
}

async function markRefused(id: string, reason: string): Promise<void> {
  await keepRefusals(new Map([[id, { reason, at: new Date().toISOString() }]]));
}

/**
 * Keep refusals so that no restart can upload them, then show them "not
 * uploaded": in the saved outbox, else as marks in prefs (Codex on #1137).
 * If neither store takes them, they are held (unsavedRefusals): shown as
 * waiting, not uploaded in this run, and tried again by the next drain.
 */
async function keepRefusals(refusals: Map<string, Refusal>): Promise<void> {
  const refuse = (list: UploadOutboxEntry[]) =>
    list.map((e) => {
      const r = refusals.get(e.id);
      return r ? { ...e, state: 'failed' as const, error: r.reason, failedAt: r.at } : e;
    });
  try {
    if (await commitDurably(refuse)) {
      for (const id of refusals.keys()) unsavedRefusals.delete(id);
      return;
    }
  } catch {
    // Not saved: the marks below.
  }
  try {
    await changeRefused((ids) => {
      for (const [id, r] of refusals) ids.set(id, r.at);
      return true;
    });
  } catch (error) {
    logger.warn('[upload-outbox] could not record a refused photo; will try again', error);
    hold(refusals);
    return;
  }
  for (const id of refusals.keys()) unsavedRefusals.delete(id);
  if (entries) {
    entries = refuse(entries);
    announce();
  }
}

/** Refusals no store took: shown as waiting, and not uploaded, until one does. */
function hold(refusals: Map<string, Refusal>): void {
  for (const [id, r] of refusals) unsavedRefusals.set(id, r);
  if (!entries?.some((e) => refusals.has(e.id) && e.state === 'failed')) return;
  entries = entries.map((e) =>
    refusals.has(e.id) ? { ...e, state: 'queued' as const, error: undefined, failedAt: undefined } : e,
  );
  announce();
}

/** Another try at keeping refusals that no store has kept yet. */
async function keepUnsavedRefusals(): Promise<void> {
  if (unsavedRefusals.size === 0) return;
  await keepRefusals(new Map(unsavedRefusals));
}

let activeOwner: (() => Promise<ActiveSession | null>) | null = null;

/**
 * The session stored now (lib/activeSessionUser.ts): a photo is uploaded only
 * under its own taker's session (Codex on #1136), with that session's token
 * (Codex on #1137). Set by jobEvidence.ts.
 */
export function configureUploadSession(active: () => Promise<ActiveSession | null>): void {
  activeOwner = active;
}

/**
 * The access token of the session stored now, if it is this owner's; null
 * otherwise; undefined when no session check is configured (tests of other
 * modules). A read that fails is not "signed out": nothing is uploaded now,
 * and the drain tries again on its backoff (Codex on #1136).
 */
async function ownerToken(ownerId: string): Promise<string | null | undefined> {
  if (!activeOwner) return undefined;
  try {
    const session = await activeOwner();
    return session?.userId === ownerId ? session.accessToken : null;
  } catch (error) {
    logger.warn('[upload-outbox] could not read whose session is stored; will try again', error);
    scheduleRetry(ownerId, 1);
    return null;
  }
}

async function isActive(ownerId: string): Promise<boolean> {
  return (await ownerToken(ownerId)) !== null;
}

/**
 * Whether another account's session is stored now (or none). A store that
 * cannot be read rejects (as Codex found for the chat outbox on #1136): it
 * may hold another account's session by now, so the photo is not kept. No
 * session check configured is no change.
 */
async function anotherAccountStored(ownerId: string): Promise<boolean> {
  if (!activeOwner) return false;
  return (await activeOwner())?.userId !== ownerId;
}

/**
 * Photos of another account, once this owner's session is confirmed:
 * sign-out drops an account's outbox, so these are left only by a wipe that
 * failed or stopped, and no screen of this account can upload or delete
 * them (Codex on #1137). Dropped once that is saved; their bytes go to the
 * trash with them.
 */
async function dropOtherAccounts(ownerId: string): Promise<void> {
  let dropped: string[] = [];
  // Under the session store lock (lib/sessionStoreLock.ts): no sign-in can
  // store another session, and so no other account can keep a photo, between
  // the check that this owner's session is the stored one and the save that
  // drops the rest (Codex on #1137).
  const saved = await underSessionStoreLock(async () => {
    if (!(await isActive(ownerId))) return false;
    return commitDurably((list) => {
      dropped = list.filter((e) => e.ownerId !== ownerId).map((e) => e.id);
      return dropped.length > 0 ? list.filter((e) => e.ownerId === ownerId) : null;
    });
  }).catch(() => false);
  if (!saved) return;
  for (const id of dropped) {
    unsavedRefusals.delete(id);
    notBefore.delete(id);
  }
  try {
    await changeRefused((ids) => dropped.filter((id) => ids.delete(id)).length > 0);
  } catch {
    // A mark left for a photo that is gone matches nothing.
  }
  await emptyTrash();
}

/** How each target is sent; set by the module that owns its endpoint. */
export function configureUploadTarget(target: UploadTarget, send: UploadOutboxDeps['send']): void {
  deps = { ...deps, [target]: send };
}

function isEntry(v: unknown): v is UploadOutboxEntry {
  const e = v as UploadOutboxEntry;
  return (
    !!e && typeof e.id === 'string' && typeof e.ownerId === 'string' && e.target === 'booking-evidence' &&
    typeof e.handle === 'string' && typeof e.createdAt === 'string' && !!e.fields && typeof e.fields === 'object' &&
    (e.state === 'queued' || e.state === 'failed') &&
    // The backoff counts on it: a missing count would retry at once, forever (Codex on #1136).
    Number.isSafeInteger(e.attempts) && e.attempts >= 0 && typeof e.fileName === 'string' &&
    Object.values(e.fields).every((v) => typeof v === 'string') &&
    (e.error === undefined || typeof e.error === 'string') && (e.failedAt === undefined || typeof e.failedAt === 'string')
  );
}

/**
 * The outbox, read once. A store that cannot be read right now rejects and is
 * read again next time: taking it as empty would let the next save overwrite
 * every photo still waiting in it. Only content that is not an outbox at all
 * starts empty.
 */
async function load(): Promise<void> {
  if (entries) return;
  loading ??= (async () => {
    const raw = await platform.storage.secure.get(UPLOAD_OUTBOX_KEY);
    let loaded: UploadOutboxEntry[] = [];
    let loadedTrash: string[] = [];
    let parsed: unknown;
    try {
      parsed = raw ? JSON.parse(raw) : [];
    } catch {
      parsed = undefined;
    }
    // A bare list is the outbox before it kept a trash.
    const saved = Array.isArray(parsed) ? { entries: parsed, trash: [] } : (parsed as { entries?: unknown; trash?: unknown } | undefined);
    let whole = false;
    if (saved && typeof saved === 'object' && Array.isArray(saved.entries)) {
      loaded = saved.entries.filter(isEntry);
      const trashList = Array.isArray(saved.trash) ? saved.trash : [];
      loadedTrash = trashList.filter((h): h is string => typeof h === 'string');
      whole = loaded.length === saved.entries.length && loadedTrash.length === trashList.length;
    }
    if (!whole) {
      // Not wholly an outbox this code saved: it is kept aside before
      // anything is saved over it, so no write discards it unread, and until
      // that is kept nothing loads (as Codex found on #1136). The photos that
      // can be read still go; one that cannot is in the copy kept aside.
      await platform.storage.secure.set(UPLOAD_OUTBOX_UNREADABLE_KEY, raw ?? '');
      logger.warn('[upload-outbox] part of the outbox could not be read; it is kept aside');
    }
    const refused = await underMarks(async () => {
      try {
        return await readRefused();
      } catch (error) {
        if (!(error instanceof UnreadableRefusalsError)) throw error;
        // Which were refused is unknown: every waiting photo counts as
        // refused, shown "not uploaded" with Try again, rather than uploaded
        // again, and the marks are rewritten to say so. Until that is saved,
        // nothing loads.
        const now = new Date().toISOString();
        const all = new Map(loaded.filter((e) => e.state === 'queued').map((e) => [e.id, now]));
        await saveRefused(all);
        return all;
      }
    });
    if (refused.size > 0) {
      loaded = loaded.map((e) => {
        const at = refused.get(e.id);
        return at && e.state === 'queued'
          ? { ...e, state: 'failed', error: e.error ?? NOT_UPLOADED_PLAIN, failedAt: e.failedAt ?? at }
          : e;
      });
    }
    if (!entries) {
      entries = loaded;
      trash = loadedTrash;
      // A list read on a retry reaches screens that were shown none while
      // the store could not be read (Codex on #1137).
      announce();
    }
    await pruneExpired();
    await emptyTrash();
  })().finally(() => {
    loading = null;
  });
  return loading;
}

function announce(): void {
  const list = entries ?? [];
  for (const cb of listeners) cb(list);
}

/** Runs `task` alone on the write chain: no other save starts until it ends. */
function exclusive<T>(task: () => Promise<T>): Promise<T> {
  const run = writes.then(task);
  writes = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

/**
 * Save a list made from the one in memory as the save runs, never an older
 * copy: a later change, or the undoing of one that failed, is never
 * overwritten by a snapshot taken before it (Codex on #1137).
 */
async function save(list: UploadOutboxEntry[] = entries ?? [], bin: string[] = trash): Promise<void> {
  await platform.storage.secure.set(UPLOAD_OUTBOX_KEY, JSON.stringify({ entries: list, trash: bin }));
}

/**
 * A change that holds only once saved: made on the list as the save runs,
 * saved with nothing else on the chain, and only then made in memory. A save
 * that fails leaves memory as it was, so no later save can keep a change the
 * caller was told failed, and nothing vanishes from the screen that is still
 * saved; it rejects. Resolves false when `change` had nothing to do (null)
 * or a sign-out came first.
 *
 * The bytes of every photo the change drops go to the trash in the same
 * save: from then on they are deleted until that succeeds, a force-stop
 * before the deletion included (Codex on #1137).
 */
function commitDurably(
  change: (list: UploadOutboxEntry[]) => UploadOutboxEntry[] | null,
  began = generation,
): Promise<boolean> {
  return exclusive(async () => {
    if (began !== generation) return false;
    const before = entries ?? [];
    const next = change(before);
    if (!next) return false;
    const kept = new Set(next.map((e) => e.handle));
    const dropped = before.filter((e) => !kept.has(e.handle)).map((e) => e.handle);
    const bin = [...new Set([...trash, ...dropped])];
    await save(next, bin);
    if (began !== generation) return false;
    entries = change(entries ?? []) ?? entries;
    trash = bin;
    announce();
    return true;
  });
}

/**
 * Deletes the bytes in the trash; a handle leaves it once its deletion
 * succeeded and that is saved. A failure is tried again at the next read or
 * drain.
 */
async function emptyTrash(): Promise<void> {
  if (trash.length === 0) return;
  const began = generation;
  const handles = [...trash];
  const results = await Promise.allSettled(handles.map((h) => platform.files.remove(h)));
  const gone = new Set(handles.filter((_, i) => results[i].status === 'fulfilled'));
  if (gone.size < handles.length) logger.warn('[upload-outbox] some photo bytes could not be deleted; will try again');
  if (gone.size === 0) return;
  await exclusive(async () => {
    if (began !== generation) return;
    const bin = trash.filter((h) => !gone.has(h));
    await save(entries ?? [], bin);
    trash = bin;
  }).catch((error) => logger.warn('[upload-outbox] could not save the outbox', error));
}

/**
 * Deletes bytes that no kept photo and no trash entry names: a force-stop
 * after files.put() but before the save of its entry leaves its handle only
 * in memory, so nothing else could find, count or delete them (Codex on
 * #1137). Once a session, before its first put: an enqueue waits for it, and
 * it does not start while a put may still be unsaved. A failure is tried
 * again at the next enqueue or drain.
 */
function sweepStrays(): Promise<void> {
  if (straysSwept === generation) return strays ?? Promise.resolve();
  if (putsInFlight > 0) return Promise.resolve();
  const began = generation;
  straysSwept = began;
  strays = (async () => {
    let failed = false;
    try {
      const handles = await platform.files.list();
      // An outbox kept aside as unreadable may still name photos: their bytes
      // stay with it, or the copy kept to recover them would name nothing
      // (Codex on #1137). A copy that cannot be read stops the sweep.
      const keptAside = await platform.storage.secure.get(UPLOAD_OUTBOX_UNREADABLE_KEY);
      if (began !== generation) return;
      const named = new Set([...(entries ?? []).map((e) => e.handle), ...trash]);
      const stray = handles.filter((h) => !named.has(h) && !keptAside?.includes(h));
      const results = await Promise.allSettled(stray.map((h) => platform.files.remove(h)));
      const left = stray.filter((_, i) => results[i].status === 'rejected');
      if (left.length > 0) {
        // Kept in the trash, so they count under the total limit and are
        // deleted again at each read and drain, a restart included (Codex on
        // #1137). Only if that save fails is the sweep run again instead.
        await exclusive(async () => {
          if (began !== generation) return;
          const bin = [...new Set([...trash, ...left])];
          await save(entries ?? [], bin);
          trash = bin;
        }).catch((error) => {
          logger.warn('[upload-outbox] could not keep stray photo bytes to delete later', error);
          failed = true;
        });
      }
    } catch (error) {
      logger.warn('[upload-outbox] could not list the kept photo bytes', error);
      failed = true;
    }
    if (failed && straysSwept === began) straysSwept = -1;
  })().finally(() => {
    sweeping = null;
  });
  sweeping = strays;
  return strays;
}

/** Bytes not kept after all (an enqueue that failed, a duplicate): deleted, else trashed. */
async function discardBytes(handle: string, began: number): Promise<void> {
  try {
    await platform.files.remove(handle);
    return;
  } catch {
    // Kept in the trash below, if that can be saved.
  }
  // Only in the session the photo was taken in (Codex on #1137): after a
  // sign-out, a trash saved now would put the ended session's outbox back
  // after its removal. The next session's stray sweep deletes the bytes.
  if (began !== generation) return;
  await exclusive(async () => {
    if (began !== generation) return;
    const bin = [...new Set([...trash, handle])];
    await save(entries ?? [], bin);
    trash = bin;
  }).catch((error) => {
    logger.warn('[upload-outbox] could not keep photo bytes to delete later', error);
    // Named nowhere now: the next enqueue or drain sweeps again and deletes
    // them as a stray (Codex on #1137).
    if (began === generation && straysSwept === began) straysSwept = -1;
  });
}

/**
 * A state change after the photo was kept (attempts, Try again): made in
 * memory at once; a failed save is logged, and the next save carries it.
 */
function update(id: string, patch: Partial<UploadOutboxEntry>): Promise<void> {
  if (!entries) return Promise.resolve();
  entries = entries.map((e) => (e.id === id ? { ...e, ...patch } : e));
  announce();
  return exclusive(() => save()).catch((error) => logger.warn('[upload-outbox] could not save the outbox', error));
}

/**
 * Drops refused photos older than FAILED_UPLOAD_TTL_MS, bytes included once
 * that is saved. Which ones is decided again inside the save: a Try again
 * saved first makes the photo waiting, and it stays (Codex on #1137).
 */
async function pruneExpired(now = Date.now()): Promise<void> {
  const expired = (e: UploadOutboxEntry) =>
    e.state === 'failed' && now - Date.parse(e.failedAt ?? e.createdAt) > FAILED_UPLOAD_TTL_MS;
  if (!(entries ?? []).some(expired)) return;
  const gone = new Set<string>();
  const saved = await commitDurably((list) => {
    const kept = list.filter((e) => !expired(e));
    if (kept.length === list.length) return null;
    for (const e of list) if (expired(e)) gone.add(e.id);
    return kept;
  }).catch(() => false);
  if (!saved) return;
  for (const id of gone) {
    unsavedRefusals.delete(id);
    notBefore.delete(id);
  }
  // Their marks go too, or the marks would only grow (Codex on #1137).
  try {
    await changeRefused((ids) => [...gone].filter((id) => ids.delete(id)).length > 0);
  } catch {
    // A mark left for a photo that is gone matches nothing.
  }
  await emptyTrash();
}

/** Taken before a photo is prepared, and handed to enqueueUpload: a sign-out in between drops it. */
export function uploadOutboxGeneration(): number {
  return generation;
}

/** Every entry; none while the store cannot be read. */
export async function uploadOutboxEntries(): Promise<readonly UploadOutboxEntry[]> {
  if (!IS_NATIVE) return [];
  try {
    await load();
  } catch (error) {
    logger.warn('[upload-outbox] could not read the outbox', error);
    return [];
  }
  return entries ?? [];
}

export function subscribeUploadOutbox(cb: (entries: readonly UploadOutboxEntry[]) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Told once per photo the function has confirmed. */
export function onUploadDone(cb: (entry: UploadOutboxEntry) => void): () => void {
  doneListeners.add(cb);
  return () => doneListeners.delete(cb);
}

/**
 * Keep a cleaned photo to upload, then try to upload it. Resolves once the
 * bytes and the entry are kept, not once uploaded.
 */
export async function enqueueUpload(input: {
  id: string;
  ownerId: string;
  target: UploadTarget;
  fields: Record<string, string>;
  file: File;
  /** uploadOutboxGeneration() from before the photo was prepared (Codex on #1137). */
  since?: number;
}): Promise<UploadOutboxEntry> {
  if (!IS_NATIVE) throw new Error('The upload outbox is app only.');
  const began = input.since ?? generation;
  if (began !== generation) throw new Error('Signed out before the photo was kept.');
  await load();
  await pruneExpired();
  await emptyTrash();
  const fullNow = fullReason(entries ?? [], input.ownerId);
  if (fullNow) throw new Error(fullNow);
  await sweepStrays();
  // A sweep another call started meanwhile finishes before this put begins.
  while (sweeping) await sweeping;
  putsInFlight += 1;
  try {
    return await keep(input, began);
  } finally {
    putsInFlight -= 1;
  }
}

/** The put and the save of one enqueue (enqueueUpload). */
async function keep(
  input: { id: string; ownerId: string; target: UploadTarget; fields: Record<string, string>; file: File },
  began: number,
): Promise<UploadOutboxEntry> {
  const handle = await platform.files.put(input.file);
  if (began !== generation) {
    // Signed out while the photo was being kept: it is not this session's.
    // Its key is gone with secure storage, so the bytes cannot be read, and
    // the next put deletes them.
    await platform.files.remove(handle).catch(() => {});
    throw new Error('Signed out before the photo was kept.');
  }
  const entry: UploadOutboxEntry = {
    id: input.id,
    ownerId: input.ownerId,
    target: input.target,
    fields: input.fields,
    handle,
    fileName: input.file.name || 'photo.jpg',
    createdAt: new Date().toISOString(),
    state: 'queued',
    attempts: 0,
  };
  // Kept before it is queued: it enters memory, where a drain or another
  // save can see it, only once its own save succeeded, so a photo the caller
  // is told was not kept is never saved by a later write pointing at bytes
  // already deleted (Codex on #1137).
  let full: string | null = null;
  let existing: UploadOutboxEntry | undefined;
  let notOwner = false;
  let kept: boolean;
  try {
    // Not kept when another account's session is stored now, checked under
    // the session store lock so no sign-in can store another in between: a
    // replacement sign-in during capture or preparation emits no SIGNED_OUT,
    // and the photo would otherwise be kept for the old account and then
    // dropped by the new one's drain (Codex on #1137). A store that cannot
    // be read does not keep it either.
    kept = await underSessionStoreLock(async () => {
      if (await anotherAccountStored(input.ownerId)) {
        notOwner = true;
        return false;
      }
      return commitDurably((list) => {
        // The same operation kept already (a capture repeated while the first
        // was being kept): that entry stands, and these bytes go (Codex on #1137).
        existing = list.find((e) => e.id === entry.id);
        if (existing) return null;
        // The list as it is when the save runs: other photos may have been kept meanwhile.
        full = fullReason(list, input.ownerId);
        return full ? null : [...list, entry];
      }, began);
    });
  } catch {
    await discardBytes(handle, began);
    throw new Error('The photo could not be kept to upload. Please try again.');
  }
  if (!kept) {
    await discardBytes(handle, began);
    if (existing && began === generation) return existing;
    if (notOwner) throw new Error('The account signed in changed before the photo was kept. Please take it again.');
    throw new Error(full ?? 'Signed out before the photo was kept.');
  }
  void drainUploadOutbox(entry.ownerId);
  return entry;
}

/**
 * Why no other photo can be kept now for this owner, if none can. Only the
 * owner's photos count: another account's, left by a sign-out whose wipe
 * failed, cannot fill it, and the owner's next drain drops them (Codex on
 * #1137).
 */
function fullReason(all: readonly UploadOutboxEntry[], ownerId: string): string | null {
  const list = all.filter((e) => e.ownerId === ownerId);
  // Only photos still waiting count here: a refused one cannot block new
  // photos for good (Codex on #1137)...
  if (list.filter((e) => e.state === 'queued').length >= UPLOAD_OUTBOX_LIMIT) {
    return 'Too many photos are waiting to upload. Reconnect, or delete some, first.';
  }
  // ...but every kept photo holds its bytes, so all of them are bounded,
  // with the bytes still waiting to be deleted.
  if (list.length + trash.length >= UPLOAD_OUTBOX_TOTAL_LIMIT) {
    return 'Too many photos were not uploaded. Delete some, then try again.';
  }
  return null;
}

/**
 * Upload a refused photo once more. False when its "not uploaded" mark could
 * not be cleared (Codex on #1136): queued anyway, a restart would read the
 * mark and turn it back to "not uploaded", silently undoing the helper's Try
 * again, so it stays "not uploaded" and the caller says so. False too when
 * the waiting state cannot be saved: the mark is put back, so a restart
 * never uploads it on its own (Codex on #1136).
 */
export async function retryUpload(id: string, ownerId: string): Promise<boolean> {
  if (!entries) return false; // a photo shown as not uploaded was read from a loaded outbox
  try {
    await clearRefusedMark(id);
  } catch (error) {
    logger.warn('[upload-outbox] could not clear a refused photo; not retried', error);
    return false;
  }
  let queued = false;
  try {
    queued = await commitDurably((list) =>
      list.some((e) => e.id === id)
        ? list.map((e) =>
            e.id === id ? { ...e, state: 'queued' as const, attempts: 0, error: undefined, failedAt: undefined } : e,
          )
        : null,
    );
  } catch (error) {
    logger.warn('[upload-outbox] could not save a retried photo; not retried', error);
  }
  if (!queued) {
    // Still "not uploaded": put back the mark that keeps a restart from
    // uploading it, or hold it if that fails too.
    const entry = entries?.find((e) => e.id === id);
    if (entry) {
      const at = entry.failedAt ?? new Date().toISOString();
      await keepRefusals(new Map([[id, { reason: entry.error ?? NOT_UPLOADED_PLAIN, at }]]));
    }
    return false;
  }
  unsavedRefusals.delete(id);
  notBefore.delete(id);
  void drainUploadOutbox(ownerId);
  return true;
}

/**
 * Drop a photo that is not uploaded, bytes included. One being uploaded is
 * waited for first (Codex on #1137): if that upload succeeds, the photo is
 * uploaded and shown as done, not deleted.
 */
export async function discardUpload(id: string): Promise<void> {
  if (!entries) return;
  while (inFlight.has(id)) await inFlight.get(id);
  if (!entries?.some((e) => e.id === id)) return;
  deleting.add(id);
  try {
    if (!(await commitDurably((list) => list.filter((e) => e.id !== id)))) return;
  } catch {
    // The saved outbox may still hold it as waiting: the refused mark is what
    // keeps a restart from uploading it, so it and the bytes stay until the
    // deletion is saved, and so does the photo, on the card, for Delete
    // again (Codex on #1137).
    return;
  } finally {
    deleting.delete(id);
  }
  unsavedRefusals.delete(id);
  notBefore.delete(id);
  await unmarkRefused(id);
  await emptyTrash();
}

/** 15 s, doubling with each attempt, up to 10 minutes. */
function backoff(attempts: number): number {
  return Math.min(15_000 * 2 ** Math.max(0, attempts - 1), 10 * 60_000);
}

function scheduleRetry(ownerId: string, attempts: number): void {
  retryIn(ownerId, backoff(attempts));
}

function retryIn(ownerId: string, delay: number): void {
  // One timer, at the soonest time asked; it drains for every account that asked.
  retryOwners.add(ownerId);
  const at = Date.now() + delay;
  if (retryTimer && retryAt <= at) return;
  if (retryTimer) clearTimeout(retryTimer);
  retryAt = at;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    const owners = [...retryOwners];
    retryOwners.clear();
    for (const owner of owners) void drainUploadOutbox(owner);
  }, delay);
}

export function drainUploadOutbox(ownerId: string | null | undefined): Promise<void> {
  if (!IS_NATIVE || !ownerId) return Promise.resolve();
  // Taken out when its pass starts, so a call during that pass runs another.
  drainOwners.delete(ownerId);
  drainOwners.add(ownerId);
  if (draining) return draining;
  draining = (async () => {
    try {
      for (let next = first(drainOwners); next !== undefined; next = first(drainOwners)) {
        drainOwners.delete(next);
        await drainOnce(next);
      }
    } finally {
      // In the same step as the last check: a call after it starts a new drain.
      draining = null;
    }
  })();
  return draining;
}

function first<T>(set: Set<T>): T | undefined {
  for (const value of set) return value;
  return undefined;
}

async function drainOnce(ownerId: string): Promise<void> {
  // A drain belongs to the session it began in (Codex on #1137).
  const began = generation;
  try {
    await load();
  } catch (error) {
    // The store cannot be read now: try again later rather than send from a guess.
    logger.warn('[upload-outbox] could not read the outbox', error);
    scheduleRetry(ownerId, 1);
    return;
  }
  // Not only at the first read: a refused photo may pass its week while the
  // app stays open (Codex on #1137).
  await pruneExpired();
  await emptyTrash();
  await sweepStrays();
  // Only as the account whose session is stored now (Codex on #1136). A
  // timer for another account leaves its photos waiting, untouched.
  if (!(await isActive(ownerId))) return;
  await dropOtherAccounts(ownerId);
  await keepUnsavedRefusals();
  if (unsavedRefusals.size > 0) scheduleRetry(ownerId, 1);
  const due = (entries ?? [])
    .filter((e) => e.ownerId === ownerId && e.state === 'queued' && !unsavedRefusals.has(e.id))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  for (const { id } of due) {
    const entry = entries?.find((e) => e.id === id);
    if (!entry || entry.state !== 'queued' || entry.ownerId !== ownerId || unsavedRefusals.has(id)) continue;
    const wait = (notBefore.get(id) ?? 0) - Date.now();
    if (wait > 0) {
      // Its own backoff after a 5xx: the photos after it go on.
      retryIn(ownerId, wait);
      continue;
    }
    const send = deps[entry.target];
    if (!send) continue;

    let blob: Blob | null;
    try {
      blob = await platform.files.open(entry.handle);
    } catch (error) {
      // A read that failed is not bytes that are gone: it waits its own
      // backoff, and only a read that finds nothing marks the photo lost
      // (Codex on #1137).
      logger.warn('[upload-outbox] could not read a waiting photo; will try again', error);
      const attempts = entry.attempts + 1;
      await update(entry.id, { attempts });
      notBefore.set(entry.id, Date.now() + backoff(attempts));
      retryIn(ownerId, backoff(attempts));
      continue;
    }
    if (!blob) {
      await refuse(entry.id, UPLOAD_LOST_MESSAGE, ownerId);
      continue;
    }
    // Reading the bytes took time: a sign-out meanwhile ends this drain
    // before anything is sent with the old session (Codex on #1137).
    if (began !== generation || !entries?.some((e) => e.id === entry.id)) return;
    const file = new File([blob], entry.fileName, { type: blob.type });

    // In flight from here: a Delete waits for this attempt (Codex on #1137).
    let handled!: () => void;
    inFlight.set(entry.id, new Promise<void>((resolve) => (handled = resolve)));
    try {
      // Checked again before each upload: the session may change mid-pass.
      // The upload carries the token read with it.
      const accessToken = await ownerToken(ownerId);
      if (accessToken === null) return;
      // A Delete or a sign-out that landed before the marker above was set
      // has been saved by now: nothing deleted is sent (Codex on #1137).
      const still = entries?.find((e) => e.id === entry.id);
      if (began !== generation || !still || still.state !== 'queued' || unsavedRefusals.has(entry.id) || deleting.has(entry.id)) {
        continue;
      }
      if ((await attempt(entry, file, send, ownerId, accessToken)) === 'stop') return;
    } finally {
      inFlight.delete(entry.id);
      handled();
    }
  }
}

/** One upload of a photo and what its answer means; 'stop' ends the pass. */
async function attempt(
  entry: UploadOutboxEntry,
  file: File,
  send: UploadOutboxDeps['send'],
  ownerId: string,
  accessToken: string | undefined,
): Promise<'next' | 'stop'> {
  let result: SendResult;
  try {
    result = await send(entry, file, accessToken);
  } catch (thrown) {
    result = { ok: false, transient: true, message: thrown instanceof Error ? thrown.message : String(thrown) };
  }

  if (result.ok) {
    let removed: boolean;
    try {
      removed = await commitDurably((list) =>
        list.some((e) => e.id === entry.id) ? list.filter((e) => e.id !== entry.id) : null,
      );
    } catch {
      // Not saved: the entry stays waiting, with its bytes, and the next
      // drain sends it again, answered by the function's replay as the
      // same photo. Done is told once that succeeds, so a restart cannot
      // tell it twice (Codex on #1137).
      scheduleRetry(ownerId, 1);
      return 'next';
    }
    if (!removed) return 'next';
    notBefore.delete(entry.id);
    for (const cb of doneListeners) cb(entry);
    await emptyTrash();
    return 'next';
  }
  if (result.transient) {
    const attempts = entry.attempts + 1;
    await update(entry.id, { attempts });
    if (result.status) {
      // An answer, so the connection works: this photo waits its own
      // backoff and the next ones are tried now (Codex on #1137).
      notBefore.set(entry.id, Date.now() + backoff(attempts));
      retryIn(ownerId, backoff(attempts));
      return 'next';
    }
    // No answer: the next photos would get none either.
    scheduleRetry(ownerId, attempts);
    return 'stop';
  }
  notBefore.delete(entry.id);
  await refuse(entry.id, result.message, ownerId);
  return 'next';
}

/**
 * A refusal, kept; one no store took is held, and a drain is scheduled to
 * keep it, or nothing might try again while the app stays open (Codex on
 * #1137).
 */
async function refuse(id: string, reason: string, ownerId: string): Promise<void> {
  await markRefused(id, reason);
  if (unsavedRefusals.has(id)) scheduleRetry(ownerId, 1);
}

/** Resolves when no drain is running; never starts one. */
export async function whenUploadOutboxIdle(): Promise<void> {
  while (draining) await draining;
}

/**
 * Sign-out: drop the copy in memory and let its writes settle. Secure
 * storage is cleared next, and with it the key the kept bytes were sealed
 * with, so the bytes are unreadable from then on; the next put deletes them.
 */
export async function forgetUploadOutbox(): Promise<void> {
  generation += 1;
  entries = [];
  trash = [];
  unsavedRefusals.clear();
  notBefore.clear();
  inFlight.clear();
  deleting.clear();
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  retryOwners.clear();
  announce();
  // A load still running may yet keep an unreadable outbox aside: it ends
  // first, so its write cannot land after the removals below and leave the
  // ended account's outbox behind (Codex on #1137).
  await loading?.catch(() => undefined);
  await writes;
  await underMarks(() => writeRefused(new Map()));
  // Its own key too, not only the bulk clear after it (as #1134 found): a
  // clear that fails is logged and sign-out goes on.
  try {
    await platform.storage.secure.remove(UPLOAD_OUTBOX_KEY);
    await platform.storage.secure.remove(UPLOAD_OUTBOX_UNREADABLE_KEY);
  } catch (error) {
    logger.warn('[upload-outbox] could not remove the outbox', error);
  }
}

// Every end of a session, idle expiry and the app lock's included.
onSessionWipe(forgetUploadOutbox);

/** Tests only: forget what is held in memory, as a force-stop would. */
export function resetUploadOutboxForTests(): void {
  entries = null;
  trash = [];
  putsInFlight = 0;
  straysSwept = -1;
  strays = null;
  sweeping = null;
  notBefore.clear();
  inFlight.clear();
  deleting.clear();
  loading = null;
  writes = Promise.resolve();
  draining = null;
  drainOwners.clear();
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  retryOwners.clear();
  listeners.clear();
  doneListeners.clear();
  unsavedRefusals.clear();
  // How targets are sent is registered at import (jobEvidence.ts) and kept.
}
